import React, { useState, useEffect } from "react";
import {
  UpdateAircraftDialogProps,
  AircraftType,
  ClassificationType,
} from "./UpdateAircraftDialog.types";
import { Button } from "../Button";
import Select from "../FormElements/Select/Select";

const AIRCRAFT_TYPES: AircraftType[] = [
  "COMMERTIAL",
  "MILITARY",
  "DRONE",
  "UNKNOWN",
];
const CLASSIFICATIONS: ClassificationType[] = [
  "FRIENDLY",
  "HOSTILE",
  "NEUTRAL",
  "UNKNOWN",
];

const UpdateAircraftDialog: React.FC<UpdateAircraftDialogProps> = ({
  isOpen,
  onClose,
  onUpdate,
  aircraft,
  theme = "dark",
  labels = {},
}) => {
  const [type, setType] = useState<string>("");
  const [classification, setClassification] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const isDark = theme === "dark";

  // Default labels
  const defaultLabels = {
    updateTitle: "Update",
    cancel: "Cancel",
    update: "Update",
    updating: "Updating...",
    close: "Close",
    type: "Type",
    classification: "Classification",
  };

  const finalLabels = { ...defaultLabels, ...labels };

  useEffect(() => {
    if (aircraft) {
      setType(aircraft.type || "");
      setClassification(aircraft.classification || "");
    }
  }, [aircraft]);

  const handleUpdate = async () => {
    if (!aircraft) return;

    setIsLoading(true);
    try {
      const updates: { type?: string; classification?: string } = {};

      if (type !== aircraft.type) {
        updates.type = type;
      }

      if (classification !== aircraft.classification) {
        updates.classification = classification;
      }

      if (Object.keys(updates).length > 0) {
        await onUpdate(updates);
      }

      onClose();
    } catch (error) {
      console.error("Error updating aircraft:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (aircraft) {
      setType(aircraft.type || "");
      setClassification(aircraft.classification || "");
    }
    onClose();
  };

  if (!isOpen) return null;

  // Backdrop style
  const backdropStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    backdropFilter: "blur(8px)",
  };

  // Dialog container style - exactly 360x314 as specified
  const dialogStyle: React.CSSProperties = {
    width: "360px",
    height: "314px",
    backgroundColor: isDark ? "#171215" : "#FFFFFF",
    borderRadius: "4px",
    position: "relative",
    boxShadow: "0px 0px 20px 0px #00000040",
    border: isDark ? "1px solid #404040" : "1px solid #E5E7EB",
    display: "flex",
    flexDirection: "column",
    overflow: "visible", // Allow dropdowns to overflow
    opacity: 1,
    transform: "rotate(0deg)",
  };

  // Header style
  const headerStyle: React.CSSProperties = {
    width: "360px",
    height: "64px",
    justifyContent: "space-between",
    opacity: 1,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: isDark ? "#404040" : "#E5E7EB",
    paddingTop: "16px",
    paddingRight: "20px",
    paddingBottom: "16px",
    paddingLeft: "20px",
    display: "flex",
    alignItems: "center",
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: isDark ? "#FFFFFF" : "#111827",
    fontFamily: "Inter, sans-serif",
  };

  // Close button style
  const closeButtonStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    padding: "8px",
    gap: "10px",
    opacity: 1,
    transition: "background-color 0.2s ease",
  };

  // Content style
  const contentStyle: React.CSSProperties = {
    width: "360px",
    height: "178px",
    padding: "20px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflow: "visible", // Allow dropdowns to overflow
  };

  // Footer style
  const footerStyle: React.CSSProperties = {
    padding: "0 20px 16px 20px", // Reduced bottom padding from 20px to 16px
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center", // Center buttons vertically
    gap: "12px",
    borderTop: `1px solid ${isDark ? "#404040" : "#E5E7EB"}`,
    paddingTop: "16px", // Reduced top padding from 20px to 16px
  };

  return (
    <div style={backdropStyle} onClick={handleCancel}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {finalLabels.updateTitle}{" "}
            {aircraft?.callsign || aircraft?.callSign || aircraft?.id}
          </h2>
          <button
            onClick={handleCancel}
            style={closeButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark
                ? "#404040"
                : "#F3F4F6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <img
              src="/Close.svg"
              alt={finalLabels.close}
              style={{
                width: "16px",
                height: "16px",
                filter: isDark ? "invert(1)" : "none",
              }}
            />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Type Field */}
          <Select
            label="Type"
            options={AIRCRAFT_TYPES.map((type) => ({
              label: type.charAt(0) + type.slice(1).toLowerCase(),
              value: type,
            }))}
            value={type}
            onChange={(value) => setType(value as string)}
            theme={theme}
            style={{ width: "320px" }}
            dropdownBackgroundColor={isDark ? "#171215" : "#FFFFFF"}
            inputBackgroundColor="transparent"
          />

          {/* Classification Field */}
          <Select
            label="Classification"
            options={CLASSIFICATIONS.map((classification) => ({
              label:
                classification.charAt(0) +
                classification.slice(1).toLowerCase(),
              value: classification,
            }))}
            value={classification}
            onChange={(value) => setClassification(value as string)}
            theme={theme}
            style={{ width: "320px" }}
            dropdownBackgroundColor={isDark ? "#171215" : "#FFFFFF"}
            inputBackgroundColor="transparent"
          />
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <Button
            variant="outline"
            onClick={handleCancel}
            theme={theme}
            style={{ minWidth: "80px" }}
          >
            {finalLabels.cancel}
          </Button>
          <Button
            variant="default"
            onClick={handleUpdate}
            theme={theme}
            style={{ minWidth: "80px" }}
          >
            {isLoading ? finalLabels.updating : finalLabels.update}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdateAircraftDialog;
